/* eslint-disable react/forbid-prop-types */
import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import H5 from '@base/H5/H5';
import { Fonts, Spacings } from '@styles/index';
import Accordion from '@base/Accordion/Accordion';
import Image from '@base/Image';
import H7 from '@base/H7/H7';
import MobileGoalCard from './MobileGoalCard';

const GoalGroupNavContainer = styled.ul`
  grid-column: span 6;
  position: sticky;
  top: 100px;
  height: calc(100vh - 100px);
  padding-inline-start: 0;
  overflow-y: auto;
  padding-bottom: 350px;
  @media (max-width: 70.95em) {
    grid-column: span 7;
  }
  @media (max-width: 47.95em) {
    height: 100%;
    position: relative;
    padding: 0;
    top: ${Spacings.SPACING_0B};
    margin: 0 -16px;
  }
`;

const StyledAccordion = styled(Accordion)`
  &.MuiAccordion-root {
    padding: ${Spacings.SPACING_4B} !important;
    margin: 0px !important;
    ${(props) =>
      props.popularGoal
        ? `margin-bottom:${Spacings.SPACING_2B} !important`
        : ''}
  }
  &.Mui-expanded {
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B} !important;
  }
  .goal-card-container {
    padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_0B};
    margin: ${Spacings.SPACING_1B} ${Spacings.SPACING_0B};
    box-shadow: none;
  }
`;

const StyledAccordionSummary = styled(Accordion.Summary)`
  &.MuiAccordionSummary-root {
    min-height: 0px;
  }
`;

const AccordionSummaryIcon = styled.div`
  position: absolute;
  right: ${Spacings.SPACING_2B};
  top: ${Spacings.SPACING_0B};
`;

const StyleAccordionDetails = styled(Accordion.Details)`
  margin-top: ${Spacings.SPACING_4B};
`;

const StyledH7 = styled(H7)`
  font-weight: ${Fonts.LIGHT};
  line-height: 18px;
`;

const RenderSummaryIcon = ({ isExpanded }) => {
  if (isExpanded) {
    return <Image width={24} height={24} src="minus.svg" alt="-" />;
  }
  return <Image width={24} height={24} src="interfaceV2.svg" alt="+" />;
};

const RenderGoalGroupItemMobile = ({
  goalGroup,
  id,
  handleAccordionClick,
  isExpanded,
  searchInFocus,
  highlightValue,
  onGoalCardClick,
  followedGoals
}) => {
  const isMyGoal = goalGroup.uid === 'my-goals';
  const handleAccordion = () => {
    if (typeof handleAccordionClick === 'function') {
      handleAccordionClick(id);
    }
  };
  const IsAccordionExpanded = isExpanded || goalGroup.name === 'Popular goals';

  return (
    <StyledAccordion
      popularGoal={goalGroup.name === 'Popular goals'}
      key={`goal-${id}-${goalGroup.uid}`}
      expanded={IsAccordionExpanded}
      onClick={handleAccordion}
      role={`goalgroupnav-mobile-${goalGroup.uid}`}
    >
      <StyledAccordionSummary>
        {!searchInFocus && goalGroup.name !== 'Popular goals' ? (
          <>
            <H5>{goalGroup.name}</H5>
            <AccordionSummaryIcon>
              <RenderSummaryIcon isExpanded={isExpanded} />
            </AccordionSummaryIcon>
          </>
        ) : (
          <StyledH7 color="var(--color-text-secondary)">
            {goalGroup.name}
          </StyledH7>
        )}
      </StyledAccordionSummary>
      {IsAccordionExpanded && (
        <StyleAccordionDetails>
          {goalGroup.children.map((goal) => {
            return (
              <MobileGoalCard
                key={`goal-${goal.uid}`}
                goal={goal}
                isMyGoal={isMyGoal}
                onGoalCardClick={onGoalCardClick}
                expandIcon
                highlightValue={highlightValue}
                role={`goal-${goal.uid}`}
                followedGoals={followedGoals}
              />
            );
          })}
        </StyleAccordionDetails>
      )}
    </StyledAccordion>
  );
};
const MobileGoalGroupNav = ({
  goalGroups,
  onGoalCardClick,
  followedGoals,
  highlightValue,
  accordionOpen,
  onGoalGroupClick,
  searchInFocus
}) => {
  const [accordionsIndex, setAccordionsIndex] = useState(
    Array(goalGroups.length).fill(false)
  );
  useEffect(() => {
    setAccordionsIndex(Array(goalGroups.length).fill(accordionOpen));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accordionOpen, goalGroups]);

  const handleAccordionClick = (index) => {
    setAccordionsIndex((prevState) => {
      const accordions = [...prevState];
      accordions[index] = !accordions[index];
      return accordions;
    });
    onGoalGroupClick(index);
  };
  return (
    <GoalGroupNavContainer>
      {goalGroups.map((goalGroup, i) => {
        // eslint-disable-next-line no-nested-ternary
        return goalGroup.children.length > 0 ? (
          // eslint-disable-next-line no-nested-ternary
          <RenderGoalGroupItemMobile
            // eslint-disable-next-line react/no-array-index-key
            key={`mobile-item-${i}-${goalGroup.uid}`}
            goalGroup={goalGroup}
            isExpanded={accordionsIndex[i]}
            handleAccordionClick={handleAccordionClick}
            searchInFocus={searchInFocus}
            onGoalCardClick={onGoalCardClick}
            id={i}
            followedGoals={followedGoals}
            highlightValue={highlightValue}
          />
        ) : null;
      })}
    </GoalGroupNavContainer>
  );
};

export default MobileGoalGroupNav;

MobileGoalGroupNav.propTypes = {
  goalGroups: PropTypes.arrayOf(PropTypes.any).isRequired,
  onGoalCardClick: PropTypes.func.isRequired,
  followedGoals: PropTypes.arrayOf(PropTypes.any),
  highlightValue: PropTypes.string,
  accordionOpen: PropTypes.bool,
  onGoalGroupClick: PropTypes.func.isRequired
};
MobileGoalGroupNav.defaultProps = {
  followedGoals: [],
  highlightValue: '',
  accordionOpen: false
};
